import { useEffect, useState } from 'react';
import type { CreateJobInput } from '@maker-toolchest/shared';
import { createJobSchema } from '@maker-toolchest/shared';
import { SlideOver } from '@/components/SlideOver';
import { Input } from '@/components/Input';
import { Alert } from '@/components/Alert';
import { FormSection } from '@/components/FormSection';
import { Button } from '@/components/Button';
import { ModelSelect } from '@/components/ModelSelect';
import { zodErrorsToFieldErrors } from '@/utils/formErrors';

interface CreateJobFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateJobInput) => Promise<void>;
  loading?: boolean;
  error?: string | null;
  defaultModelId?: string;
}

const defaultFormData = {
  jobType: 'PRINT_3D', // Always default to 3D printing for now
  name: '',
  description: '',
  quantity: '1',
  modelId: '',
};

export function CreateJobForm({
  open,
  onClose,
  onSubmit,
  loading = false,
  error,
  defaultModelId,
}: CreateJobFormProps) {
  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setFormData({
        ...defaultFormData,
        modelId: defaultModelId || '',
      });
      setErrors({});
    }
  }, [open, defaultModelId]);

  const handleChange = (field: keyof typeof defaultFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (data: CreateJobInput): boolean => {
    // Validate with Zod schema
    const result = createJobSchema.safeParse(data);

    if (!result.success) {
      const validationErrors = zodErrorsToFieldErrors(result.error);
      setErrors(validationErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data: CreateJobInput = {
      jobType: formData.jobType as CreateJobInput['jobType'],
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      quantity: parseInt(formData.quantity, 10),
      modelId: formData.modelId || undefined,
    };

    if (!validate(data)) return;

    await onSubmit(data);
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Create New Job"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-job-form"
            disabled={loading}
            loading={loading}
          >
            Create Job
          </Button>
        </>
      }
    >
      <form id="create-job-form" onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <Alert variant="error" message={error} />
        )}

        <FormSection
          title="Job Details"
          description="Basic information about the job"
        >
            <div className="space-y-4">
              {/* Job Type hidden for now - always defaults to PRINT_3D */}

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1">
                  Job Name *
                </label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter job name"
                  maxLength={200}
                  disabled={loading}
                  error={errors.name}
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Optional description"
                  maxLength={1000}
                  rows={4}
                  disabled={loading}
                  className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-foreground-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div>
                <label htmlFor="quantity" className="block text-sm font-medium text-foreground mb-1">
                  Quantity *
                </label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.quantity}
                  onChange={(e) => handleChange('quantity', e.target.value)}
                  disabled={loading}
                  error={errors.quantity}
                />
                <p className="mt-1 text-xs text-foreground-muted">
                  Number of tasks to create (1-100)
                </p>
              </div>

              <div>
                <label htmlFor="modelId" className="block text-sm font-medium text-foreground mb-1">
                  Model (optional)
                </label>
                <ModelSelect
                  value={formData.modelId}
                  onChange={(value) => handleChange('modelId', value)}
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-foreground-muted">
                  Associate this job with a 3D model
                </p>
              </div>
            </div>
          </FormSection>
        </form>
    </SlideOver>
  );
}
